import React, { useEffect, useState } from 'react';

import { Grid, Container, Typography, makeStyles, useMediaQuery } from '@material-ui/core';
import queryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';

import AdditionalFilesList from '../../components/additionalFiles/AdditionalFilesList';
import AdditionalFiles from '../../components/caseActions/AdditionalFiles';
import CaseReportButton from '../../components/caseActions/CaseReportButton';
import PatientReportDetail from '../../components/patientReportDetail/PatientReportDetail';
import PatientReportNotes from '../../components/patientReportNotes/PatientReportNotes';
import PatientReportQuestion from '../../components/patientReportQuestion/PatientReportQuestion';
import PatientReportVideo from '../../components/patientReportVideo/PatientReportVideo';
import PatientShareIcon from '../../components/patientShareIcon/PatientShareIcon';
import PatientSuppliedFiles from '../../components/patientSuppliedFiles/PatientSuppliedFiles';
import PatientVideoReportRating from '../../components/patientVideoReportRating/PatientVideoReportRating';
import UndoSignOffButton from '../../components/undoSignOffButton/undoSignOffButton';
import VideoPlaylist from '../../components/videoPlaylist/VideoPlaylist';
import useBlocRefHook from '../../utils/useBlocRef.hook';

const useStyles = makeStyles((theme) => ({
  additionalFilesContainer: {
    marginTop: '30px',
    [theme.breakpoints.up('lg')]: {
      marginTop: '60px',
    },
  },
  generalContainer: {
    [theme.breakpoints.up('lg')]: {
      paddingLeft: '70px',
      paddingRight: '110px',
    },
  },
  noteContainer: {
    paddingBottom: '40px',
    paddingTop: '1rem',
    [theme.breakpoints.down('sm')]: {
      borderBottom: '1px solid #E7E7E7',
      marginTop: 15,
      minHeight: 40,
      paddingBottom: '60px',
      paddingTop: 15,
    },
  },
  patientRatingContainer: {
    marginTop: '30px',
    [theme.breakpoints.up('lg')]: {
      marginTop: '60px',
    },
  },
  patientSuppliedFilesContainer: {
    marginTop: '30px',
    [theme.breakpoints.up('lg')]: {
      marginTop: '40px',
    },
  },
  rightContentContainer: {
    boxShadow: 'none',
    padding: '0px',
    [theme.breakpoints.up('lg')]: {
      boxShadow: '0px 0px 50px rgba(0, 0, 0, 0.1)',
      height: '100%',
      padding: '20px 46px 20px 20px',
    },
  },
  rightContentParentContainer: {
    display: 'none',
    width: '400px',
    [theme.breakpoints.up('lg')]: {
      display: 'block',
    },
  },
  secondaryContentContainer: {
    display: 'block',
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
  },
  title: {
    color: '#172B4D',
    fontSize: '18px',
    fontWeight: '500',
    lineHeight: '27px',
    [theme.breakpoints.up('md')]: {
      fontSize: '24px',
      lineHeight: '36px',
    },
  },
  titleContainer: {
    margin: '16px 0px',
    [theme.breakpoints.up('md')]: {
      margin: '30px 0px 16px',
    },
  },
  videoContainer: {
    paddingBottom: '1rem',
    [theme.breakpoints.down('sm')]: {
      padding: '0 !important',
    },
  },
  videoGridContainer: {
    [theme.breakpoints.down('sm')]: {
      padding: '0 !important',
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: '70px',
      paddingRight: '110px',
    },
  },
  videoPlayListContainer: {
    maxHeight: '500px',
    overflowX: 'hidden',
    overflowY: 'auto',
    width: '100%',
  },
  videoPlayListGridContainer: {
    paddingTop: '1rem',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      paddingLeft: '0 !important',
      paddingTop: 15,
    },
  },
}));

const useDialogStyles = makeStyles((theme) => ({
  paper: {
    [theme.breakpoints.up('md')]: {
      minWidth: '50vw',
    },
    [theme.breakpoints.down('sm')]: {
      height: '100%',
      margin: 0,
      maxHeight: '100%',
      width: '100%',
    },
  },
}));

export default () => {
  const classes = useStyles();
  const dialogClasses = useDialogStyles();
  const history = useHistory();
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const [caseDetails, setCaseDetails] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [reportRate, setReportRate] = useState({
    ratingComment: '',
    ratingScore: 0,
  });
  const blocRef = useBlocRefHook('patientReportBloc');
  const { search } = useLocation();
  const { recordId, caseId } = queryString.parse(search);

  useEffect(() => {
    const stateSub = blocRef.current.caseDetails$.subscribe(setCaseDetails);
    const videoSub = blocRef.current.selectedVideo$.subscribe(setSelectedVideo);
    const reportRateSub = blocRef.current.reportRate$.subscribe(setReportRate);
    const userDetailsSub = blocRef.current.userDetails$.subscribe(setUserDetails);

    if (caseId) {
      blocRef.current.getRecordByCaseId(caseId);
    } else if (recordId) {
      blocRef.current.getRecordById(recordId);
    } else {
      history.push('/');
    }
    return () => {
      stateSub.unsubscribe();
      videoSub.unsubscribe();
      reportRateSub.unsubscribe();
      userDetailsSub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isOwner = caseDetails?.case?.patientId === userDetails?.id;

  const handleOnSelect = (video) => blocRef.current.selectVideo(video);
  const handleViewReport = () => blocRef.current.viewReport(dialogClasses);
  const handleViewStudy = () => blocRef.current.viewStudy();
  const handleReportRateChange = (field, value) => blocRef.current.onReportRateChange(field, value);
  const handleSubmitReportRate = () => blocRef.current.submitReportRate();
  const handleOpenSharePopup = () => blocRef.current.handleOpenSharePopup();
  const handleRevertSignOffPopup = () => blocRef.current.handleRevertSignOffPopup();
  const handlePlayVideo = () => blocRef.current.handlePlayVideo();
  const handleViewMoreAdditionalFiles = (files) => blocRef.current.openViewMoreAdditionalFilesDialog(files);
  const handleViewAdditionalFile = (file) => blocRef.current.openViewer(file);

  const renderSecondaryContent = () => (
    <div className={classes.rightContentContainer}>
      <VideoPlaylist videos={caseDetails?.videos} onSelect={handleOnSelect} />

      <CaseReportButton caseReport={caseDetails?.case?.caseReport} readMode caseStatus={caseDetails?.case?.status.value} />

      <div className={classes.patientSuppliedFilesContainer}>
        <PatientSuppliedFiles onViewReport={handleViewReport} onViewStudy={handleViewStudy} caseDetails={caseDetails} />
      </div>

      <div className={classes.additionalFilesContainer}>
        <AdditionalFilesList caseDetails={caseDetails} onViewMoreAdditionalFiles={handleViewMoreAdditionalFiles} onViewAdditionalFile={handleViewAdditionalFile} />
      </div>

      {userDetails?.role === 'super-admin' ? <AdditionalFiles caseData={caseDetails.case} /> : null}

      {isOwner ? (
        <div className={classes.patientRatingContainer}>
          <PatientVideoReportRating
            loadingButton$={blocRef.current.loadingReportRate$}
            reportRate={reportRate}
            onReportRateChange={handleReportRateChange}
            onSubmit={handleSubmitReportRate}
          />
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      {caseDetails && (
        <Grid container>
          <Grid item style={{ flex: '1', maxWidth: '100%' }}>
            <div className="pb-2">
              {/* Title */}
              <Container className={classes.generalContainer}>
                <div className={classes.titleContainer}>
                  <Grid container alignItems={isDesktop ? 'flex-end' : 'center'} justify="space-between">
                    <Grid item>
                      <Typography className={classes.title} style={{ fontWeight: '500' }} align="left">
                        {caseDetails?.case?.patient?.name} | <span style={{ color: '#999999' }}>{caseDetails?.case?.title || caseDetails?.case?.description} </span>
                      </Typography>
                    </Grid>

                    <Grid item className={classes.buttonsContainer}>
                      {userDetails?.role === 'super-admin' && <UndoSignOffButton onClick={handleRevertSignOffPopup} />}
                      <PatientShareIcon onClick={handleOpenSharePopup} />
                    </Grid>
                  </Grid>
                </div>
              </Container>

              {/* Video */}
              <Container className={classes.videoGridContainer}>
                <div className={`w-100 ${classes.videoContainer}`}>
                  <PatientReportVideo video={selectedVideo} onPlayVideo={handlePlayVideo} />
                </div>
              </Container>

              {/* Report Detail */}
              <Container className={classes.generalContainer}>
                <PatientReportDetail video={selectedVideo}>
                  {caseDetails?.note && <PatientReportNotes notes={caseDetails?.note} />}
                  <PatientReportQuestion question={caseDetails?.case.questionText} caseDetails={caseDetails?.case} />
                </PatientReportDetail>
              </Container>

              <Container className={classes.secondaryContentContainer}>{renderSecondaryContent()}</Container>
            </div>
          </Grid>

          <Grid item className={classes.rightContentParentContainer}>
            {renderSecondaryContent()}
          </Grid>
        </Grid>
      )}
    </>
  );
};
